import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { useLayout } from "@/../_metronic/layout/core";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import { TbReportSearch } from "react-icons/tb";

import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { User } from "@/@types/sintegrada/User";
import {
	getReceptionDashboard,
	ReceptionDashboardData,
} from "@/services/sintegrada/attendance/getReceptionDashboard";
import { getProfissionalReception } from "@/services/sintegrada/professional/getProfissionalReception";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { ReceptionDashboardFilters } from "./ReceptionDashboardFilters";
import { AreaChart } from "@/components/Charts/AreaChart";
import { BarChart } from "@/components/Charts/BarChart";
import { DonutChart } from "@/components/Charts/DonutChart";
import { ReceptionDashboardCards } from "./ReceptionDashboardCards";
import { LoadingScreen } from "@/components/LoadingScreen";
import { PrintComponent } from "@/components/PrintComponent";

dayjs.extend(utc);
dayjs.extend(timezone);

export function ReceptionDashboard() {
	const { uf, ibgeCode, cnes } = useCurrentAccount();
	const { setTitle } = useLayout();
	const [startDate, setStartDate] = useState(
		dayjs().add(-1, "month").startOf("day").format("YYYY-MM-DDTHH:mm")
	);
	const [endDate, setEndDate] = useState(dayjs().endOf("day").format("YYYY-MM-DDTHH:mm"));
	const [isLoading, setIsLoading] = useState(false);
	const [minAge, setMinAge] = useState<number>();
	const [maxAge, setMaxAge] = useState<number>();
	const [professionalId, setProfessionalId] = useState<number>();
	const [professionals, setProfessionals] = useState<User[]>([]);
	const [dashboard, setDashboard] = useState<ReceptionDashboardData>();
	const printPageRef = useRef<HTMLDivElement | null>(null);

	const dashboardData = dashboard || {
		aggregatedByCity: [],
		aggregatedByDayOfWeek: [],
		aggregatedByGender: [],
		aggregatedByHealthUnit: [],
		aggregatedByNeighborhood: [],
		aggregatedByPeriod: [],
		averageAttendancesPerDay: 0,
		evasions: 0,
		totalAttendances: 0,
		obitos: 0,
		obitos_ate_24h: 0,
		obitos_maior_24h: 0,
		maxDay: 0,
		minDay: 0,
		por_turno: [],
	};

	const printPage = useReactToPrint({
		content: () => printPageRef.current,
	});

	async function fetchDashboard() {
		setIsLoading(true);
		setDashboard(
			await getReceptionDashboard({
				uf,
				ibgeCode,
				cnes,
				startDate: dayjs(startDate)
					.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
					.startOf("minute")
					.format("YYYY-MM-DDTHH:mm:ssZ"),
				endDate: dayjs(endDate)
					.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
					.endOf("minute")
					.format("YYYY-MM-DDTHH:mm:ssZ"),
				minAge,
				maxAge,
				professionalId,
			})
		);
		setIsLoading(false);
	}

	async function fetchProfessional() {
		setProfessionals(await getProfissionalReception({ uf, ibgeCode, cnes }));
	}

	async function fetch() {
		await fetchDashboard();
		await fetchProfessional();
	}

	useEffect(() => {
		setTitle("Monitoramento - Recepção");
		fetch();
		return () => {
			setIsLoading(false);
			setDashboard(undefined);
			setProfessionals([]);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="p-2">
			<LoadingScreen loading={isLoading} />
			<Row className="mx-1">
				<Card className="h-100 shadow-sm">
					<Card.Body>
						<ReceptionDashboardFilters
							startDate={startDate}
							setStartDate={setStartDate}
							endDate={endDate}
							setEndDate={setEndDate}
							setMinAge={setMinAge}
							setMaxAge={setMaxAge}
							setProfessionalId={setProfessionalId}
							professionals={professionals}
							handleFilter={fetchDashboard}
							handlePrint={printPage}
						/>
					</Card.Body>
				</Card>
			</Row>
			<div>
				<Row className="mx-1 mt-3">
					<Card className="bg-white shadow-sm">
						<Card.Header className="p-0">
							<Card.Title className="d-flex align-items-center gap-2 p-2 text-primary mb-0">
								<TbReportSearch
									size={30}
									className="bg-primary text-white rounded p-1 me-2"
								/>
								Recepção
							</Card.Title>
						</Card.Header>
					</Card>
				</Row>
				<PrintComponent
					printComponentRef={printPageRef}
					headerContainer={
						<div>
							<p className="text-center fs-3">Relatório da recepção</p>
							{startDate && endDate && (
								<div>
									<p className="text-center">
										<span>{`${dayjs(startDate).format("DD/MM/YYYY")} Á ${dayjs(
											endDate
										).format("DD/MM/YYYY")}`}</span>
									</p>
								</div>
							)}
							{minAge && maxAge && (
								<p>
									<strong>Faixa etária:</strong>
									<span>{`${minAge} anos á ${maxAge} anos`}</span>
								</p>
							)}
							{professionalId && (
								<p>
									<strong>Profissional:</strong>
									<span>
										{professionals.find((p) => p.id === professionalId)?.name}
									</span>
								</p>
							)}
						</div>
					}
				>
					<Row className="ms-3 mt-3">
						<ReceptionDashboardCards
							totalAttendances={dashboardData.totalAttendances}
							averageAttendancesPerDay={dashboardData.averageAttendancesPerDay}
							evasions={dashboardData.evasions}
							evasionsPercentage={calculatePercentage(
								dashboardData.evasions,
								dashboardData.totalAttendances
							)}
							obitos={dashboardData.obitos}
							obitos_ate_24h={dashboardData.obitos_ate_24h}
							obitos_maior_24h={dashboardData.obitos_maior_24h}
							maxDay={dashboardData.maxDay}
							minDay={dashboardData.minDay}
							por_turno={dashboardData.por_turno}
						/>
					</Row>
					<Row className="p-2 mt-3 page-break-before">
						<Col sm={12} lg={12} className="mb-2">
							<Card className="h-100 shadow-sm">
								<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
									<Card.Title>Pacientes recepcionados por período</Card.Title>
								</Card.Header>
								<Card.Body>
									<AreaChart
										categories={dashboardData.aggregatedByPeriod.map(
											(aggregated) => aggregated.key
										)}
										series={[
											{
												name: "Atendimentos",
												data: dashboardData.aggregatedByPeriod.map(
													(aggregated) => aggregated.total
												),
											},
										]}
									/>
								</Card.Body>
							</Card>
						</Col>
					</Row>
					<Row className="p-2 page-break-before">
						<Col sm={12} lg={12} className="mb-2">
							<Card className="h-100 shadow-sm">
								<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
									<Card.Title>
										Volume de atendimentos por dia da semana
									</Card.Title>
								</Card.Header>
								<Card.Body className="d-flex justify-content-center align-items-center">
									<BarChart
										categories={dashboardData.aggregatedByDayOfWeek.map(
											(aggregated) => aggregated.key
										)}
										series={[
											{
												name: "Atendimentos",
												data: dashboardData.aggregatedByDayOfWeek.map(
													(aggregated) => aggregated.total
												),
											},
										]}
										colors={["#04e69a"]}
										categoryTextWidth={400}
										baseMultiplier={10}
										isVertical
									/>
								</Card.Body>
							</Card>
						</Col>
					</Row>
					<Row className="p-2 page-break-before">
						<Col sm={12} lg={6} className="mb-2">
							<Card className="h-100 shadow-sm">
								<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
									<Card.Title>
										Top 10 - Pacientes recepcionados por município
									</Card.Title>
								</Card.Header>
								<Card.Body className="d-flex justify-content-center align-items-center">
									<BarChart
										categories={dashboardData.aggregatedByCity
											.slice(0, 10)
											.map((aggregated) => aggregated.key || "")}
										series={[
											{
												name: "Atendimentos",
												data: dashboardData.aggregatedByCity
													.slice(0, 10)
													.map((aggregated) => aggregated.total),
											},
										]}
										categoryTextWidth={400}
									/>
								</Card.Body>
							</Card>
						</Col>
						<Col sm={12} lg={6} className="mb-2">
							<Card className="h-100 shadow-sm">
								<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
									<Card.Title>
										{" "}
										Top 10 - Pacientes recepcionados por bairro
									</Card.Title>
								</Card.Header>
								<Card.Body className="d-flex justify-content-center align-items-center">
									<BarChart
										categories={dashboardData.aggregatedByNeighborhood.map(
											(aggregated) => aggregated.key || ""
										)}
										series={[
											{
												name: "Atendimentos",
												data: dashboardData.aggregatedByNeighborhood.map(
													(aggregated) => aggregated.total
												),
											},
										]}
										categoryTextWidth={400}
										colors={["#04e69a"]}
									/>
								</Card.Body>
							</Card>
						</Col>
					</Row>
					<Row className="p-2 page-break-before">
						<Col sm={12} lg={6} className="mb-2">
							<Card className="h-100 shadow-sm">
								<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
									<Card.Title>
										Top 10 - Pacientes recepcionados por UBS
									</Card.Title>
								</Card.Header>
								<Card.Body className="d-flex justify-content-center align-items-center">
									<BarChart
										categories={dashboardData.aggregatedByHealthUnit.map(
											(aggregated) => aggregated.key
										)}
										series={[
											{
												name: "Atendimentos",
												data: dashboardData.aggregatedByHealthUnit.map(
													(aggregated) => aggregated.total
												),
											},
										]}
										disableXaxisLabel={true}
										categoryTextWidth={300}
									/>
								</Card.Body>
							</Card>
						</Col>
						<Col sm={12} lg={6}>
							<Card className="h-100 shadow-sm">
								<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
									<Card.Title>Pacientes recepcionados por gênero</Card.Title>
								</Card.Header>
								<Card.Body className="d-flex justify-content-center align-items-center">
									<DonutChart
										labels={dashboardData.aggregatedByGender.map(
											(aggregated) => aggregated.key
										)}
										series={dashboardData.aggregatedByGender.map(
											(aggregated) => aggregated.total
										)}
									/>
								</Card.Body>
							</Card>
						</Col>
					</Row>
					<Row className="p-2 page-break-before">
						<Col sm={12} lg={12}>
							<Card className="h-100 shadow-sm">
								<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
									<Card.Title>Pacientes recepcionados por turno</Card.Title>
								</Card.Header>
								<Card.Body className="d-flex justify-content-center align-items-center">
									<BarChart
										categories={dashboardData.por_turno.map(
											(aggregated) => aggregated.turno
										)}
										series={[
											{
												name: "Atendimentos",
												data: dashboardData.por_turno.map(
													(aggregated) => aggregated.total
												),
											},
										]}
										colors={["#04e69a"]}
										categoryTextWidth={400}
										baseMultiplier={10}
										isVertical
									/>
								</Card.Body>
							</Card>
						</Col>
					</Row>
				</PrintComponent>
			</div>
		</div>
	);
}
